import { useState, useEffect } from "react";
import { getDepartmentHeatmap } from "../api/DepartmentHeatmapApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Shield, AlertTriangle, Users, Building2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const DepartmentHeatmap = () => {
  const [departments, setDepartments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectLoading, setSelectLoading] = useState(false);;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (departmentId = null) => {
    if (departmentId) {
      setSelectLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await getDepartmentHeatmap(departmentId);
      const data = response.data || {};

      const formattedData = (data.departments || []).map((dept) => ({
        id: dept.id,
        name: dept.name,
        employee_count: Number(dept.employee_count) || 0,
        risk_score: Number(dept.risk_score) || 0,
        phishing_fail_rate: Number(dept.phishing_fail_rate) || 0,
        sensitivity_level: Number(dept.sensitivity_level) || 1,
        last_simulation: dept.last_simulation || null,
      }));

      setDepartments(formattedData);
      setSummary(data.summary || null);

      if (data.selected_department) {
        const sd = data.selected_department;
        setSelectedDept({
          id: sd.id,
          name: sd.name,
          employee_count: Number(sd.employee_count) || 0,
          risk_score: Number(sd.risk_score) || 0,
          phishing_fail_rate: Number(sd.phishing_fail_rate) || 0,
          sensitivity_level: Number(sd.sensitivity_level) || 1,
          last_simulation: sd.last_simulation || null,
        });
      }
    } catch (error) {
      console.error("Department Heatmap Error:", error);
      toast.error("Failed to load department data");
    }

    setLoading(false);
    setSelectLoading(false);
  };



  const handleSelectDepartment = (deptId) => {
    fetchData(deptId);
  };

  const getRiskColor = (riskScore) => {
    if (riskScore <= 25) return "#1fa7a1"; // Low - Teal
    if (riskScore <= 40) return "#22c55e"; // Low-Medium - Green
    if (riskScore <= 55) return "#f59e0b"; // Medium - Amber
    if (riskScore <= 70) return "#f97316"; // Medium-High - Orange
    return "#ef4444"; // High - Red
  };

  const getRiskLabel = (riskScore) => {
    if (riskScore <= 25) return "Low Risk";
    if (riskScore <= 40) return "Low-Medium";
    if (riskScore <= 55) return "Medium Risk";
    if (riskScore <= 70) return "Medium-High";
    return "High Risk";
  };

  // Create 5x5 grid based on sensitivity and fail rate
  const createHeatmapGrid = () => {
    const grid = Array.from({ length: 5 }, () => Array(5).fill(null));

    (departments || []).forEach((dept) => {
      const failRate = Number(dept?.phishing_fail_rate ?? 0);
      const sensitivity = Number(dept?.sensitivity_level ?? 1);

      const xIndex = Math.max(0, Math.min(4, Math.floor(failRate * 5 * 0.99)));
      const yIndex = Math.max(0, Math.min(4, sensitivity - 1));

      if (!grid[4 - yIndex]) return;

      if (!grid[4 - yIndex][xIndex]) {
        grid[4 - yIndex][xIndex] = [];
      }

      grid[4 - yIndex][xIndex].push(dept);
    });

    return grid;
  };

  const heatmapGrid = createHeatmapGrid();

  if (loading) {
    return <CulsightPageLoader />;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="department-heatmap-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Department Risk Heatmap</h1>
        <p className="text-gray-500 text-sm mt-1">Visualize department risk by sensitivity and phishing fail rate</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Departments", value: summary?.total_departments || 0, icon: Building2, color: "#8c3add" },
          { label: "High Risk", value: summary?.high_risk || 0, icon: AlertTriangle, color: "#ef4444" },
          { label: "Low Risk", value: summary?.low_risk || 0, icon: Shield, color: "#1fa7a1" },
          { label: "Total Employees", value: summary?.total_employees || 0, icon: Users, color: "#f59e0b" }
        ].map((stat, index) => (
          <Card key={index} className="border border-gray-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${stat.color}15` }}>
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1f1f23]">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Heatmap */}
        <Card className="lg:col-span-2 border border-gray-100 shadow-sm" data-testid="heatmap-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Risk Matrix</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="absolute -left-14 top-1/2 -translate-y-1/2 -rotate-90 text-xs text-gray-500 font-medium whitespace-nowrap">
              Sensitivity Level (Low → High)
            </div>
            <div className="relative">
              {/* Y-axis label */}


              {/* Grid */}
              <div className="ml-8">
                <div className="grid grid-cols-5 gap-2" data-testid="heatmap-grid">
                  {heatmapGrid.map((row, rowIndex) => (
                    row.map((cell, colIndex) => {
                      const depts = cell || [];
                      const avgRisk = depts.length > 0
                        ? depts.reduce((sum, d) => sum + d.risk_score, 0) / depts.length
                        : 0;
                      const cellColor = depts.length > 0 ? getRiskColor(avgRisk) : "#f3f4f6";

                      return (
                        <TooltipProvider key={`${rowIndex}-${colIndex}`}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className="aspect-square rounded-lg flex items-center justify-center cursor-pointer heatmap-cell transition-all hover:scale-105"
                                style={{
                                  backgroundColor: cellColor,
                                  opacity: depts.length > 0 ? 1 : 0.3
                                }}
                                onClick={() => depts.length > 0 && handleSelectDepartment(depts[0].id)}
                                data-testid={`heatmap-cell-${rowIndex}-${colIndex}`}
                              >
                                {depts.length > 0 && (
                                  <span className="text-white font-bold text-sm">
                                    {depts.length}
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="bg-white border shadow-lg p-3">
                              {depts.length > 0 ? (
                                <div>
                                  {depts.map((d, i) => (
                                    <div key={i} className="mb-2 last:mb-0">
                                      <p className="font-semibold text-[#1f1f23]">{d.name}</p>
                                      <p className="text-xs text-gray-500">
                                        Risk: {d.risk_score}% | Fail Rate: {(d.phishing_fail_rate * 100).toFixed(0)}%
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-gray-500">No departments</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })
                  ))}
                </div>

                {/* X-axis label */}
                <p className="text-center text-xs text-gray-500 font-medium mt-4">
                  Phishing Fail Rate (Low → High)
                </p>
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-500">Risk Level:</span>
                {[
                  { color: "#1fa7a1", label: "Low" },
                  { color: "#22c55e", label: "Low-Med" },
                  { color: "#f59e0b", label: "Medium" },
                  { color: "#f97316", label: "Med-High" },
                  { color: "#ef4444", label: "High" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-gray-600">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Department List */}
        <Card className="border border-gray-100 shadow-sm" data-testid="department-list">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Departments</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              {departments.sort((a, b) => b.risk_score - a.risk_score).map((dept) => (
                <div
                  key={dept.id}
                  className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${selectedDept?.id === dept.id ? 'bg-[#8c3add]/5' : ''
                    }`}
                  onClick={() => handleSelectDepartment(dept.id)}
                  data-testid={`dept-item-${dept.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#1f1f23]">{dept.name}</p>
                      <p className="text-xs text-gray-500">{dept.employee_count} employees</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold" style={{ color: getRiskColor(dept.risk_score) }}>
                        {dept.risk_score}%
                      </p>
                      <Badge
                        className="text-xs mt-1"
                        style={{
                          backgroundColor: `${getRiskColor(dept.risk_score)}15`,
                          color: getRiskColor(dept.risk_score)
                        }}
                      >
                        {getRiskLabel(dept.risk_score)}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{
                        width: `${dept.risk_score}%`,
                        backgroundColor: getRiskColor(dept.risk_score)
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selected Department Detail */}
      {selectedDept && (
        <Card className="border border-gray-100 shadow-sm" data-testid="dept-detail">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Building2 size={20} className="text-[#8c3add]" />
              {selectedDept.name} Department
              {selectLoading && (
                <span className="text-xs text-gray-400 font-normal ml-2">Refreshing...</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">Employees</p>
                <p className="text-2xl font-bold text-[#1f1f23]">{selectedDept.employee_count}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">Risk Score</p>
                <p className="text-2xl font-bold" style={{ color: getRiskColor(selectedDept.risk_score) }}>
                  {selectedDept.risk_score}%
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">Phishing Fail Rate</p>
                <p className="text-2xl font-bold text-[#f59e0b]">
                  {(selectedDept.phishing_fail_rate * 100).toFixed(0)}%
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">Sensitivity Level</p>
                <p className="text-2xl font-bold text-[#8c3add]">{selectedDept.sensitivity_level}/5</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DepartmentHeatmap;
